import { useState } from 'react';
import { boundedText, maxLengthLimitError } from '../../utils/caseFieldValidation';
import { formatUploadTimeLabel } from '../../utils/dateFormat';
import { displayClientFileName } from '../../utils/fileDisplayName';
import { useTranslation } from 'react-i18next';

export const DOC_SKIP_REASON_MAX = 255;

export type DocumentUploadRowData = {
  id: string;
  documentName: string;
  mandatory: boolean;
  files?: { fileName: string; originalFileName: string; uploadTime: string }[];
  fileName?: string;
  originalFileName?: string;
  uploadTime?: string;
  // files: { fileName: string; originalFileName: string; uploadTime: string }[];
  skipReason: string;
};

function CloudUploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function FileDocIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function PlusCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="ccr-err" style={{ marginTop: 4 }}>
      {message}
    </div>
  );
}

function inpClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}

type Props = {
  doc: DocumentUploadRowData;
  multipleAllowed?: boolean;
  uploading: boolean;
  reasonError?: string;
  reasonPlaceholder?: string;
  idPrefix?: string;
  onUploadClick: () => void;
  onPreview: (fileIndex: number) => void;
  onRemove: (fileIndex: number) => void;
  onReasonChange: (reason: string) => void;
};

export function DocumentUploadRow({
  doc,
  multipleAllowed = false,
  uploading,
  reasonError,
  reasonPlaceholder,
  idPrefix = 'doc',
  onUploadClick,
  onPreview,
  onRemove,
  onReasonChange,
}: Props) {
  const { t } = useTranslation();
  const [limitError, setLimitError] = useState('');
  const resolvedReasonPlaceholder = reasonPlaceholder ?? t('wizard.placeholders.docSkipReason');
  const files = Array.isArray(doc.files)
    ? doc.files
    : doc.fileName
    ? [{ fileName: doc.fileName, originalFileName: doc.originalFileName ?? '', uploadTime: doc.uploadTime ?? '' }]
    : [];
  const hasFile = files.length > 0;
  const reasonDisplayError = limitError || reasonError;

  return (
    <div className="ccr-doc-upload-row">
      <div className="ccr-doc-upload-label">
        {doc.documentName}
        {doc.mandatory ? <span className="req"> *</span> : <span className="opt"> </span>}
      </div>

      <div>
        {files.length === 0 ? (
          <button type="button" className="ccr-doc-upload-btn" disabled={uploading} onClick={onUploadClick}>
            <CloudUploadIcon />
            {uploading ? t('wizard.documents.uploading') : t('wizard.placeholders.uploadDocument')}
          </button>
        ) : (
          files.map((f, i) => {
            const displayName = displayClientFileName(f.originalFileName, f.fileName);
            const uploadTime = formatUploadTimeLabel(f.uploadTime);
            const isLast = i === files.length - 1;
            return (
              <div key={i} className="ccr-doc-upload-file-wrap">
                <div className="ccr-doc-upload-file">
                  <div className="ccr-doc-upload-file-ico">
                    <FileDocIcon />
                  </div>
                  <div className="ccr-doc-upload-file-body">
                    <div className="ccr-doc-upload-file-name" title={displayName}>
                      {displayName}
                    </div>
                    {uploadTime ? <div className="ccr-doc-upload-file-meta">{uploadTime}</div> : null}
                  </div>
                  <div className="ccr-doc-upload-file-acts">
                    <button
                      type="button"
                      className="act-b"
                      aria-label={`Preview ${displayName}`}
                      data-tip="Preview"
                      onClick={() => onPreview(i)}
                    >
                      <EyeIcon />
                    </button>
                    <button
                      type="button"
                      className="act-b del"
                      aria-label={`Remove ${displayName}`}
                      data-tip="Delete"
                      onClick={() => onRemove(i)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                {isLast && multipleAllowed && (
                  <button
                    type="button"
                    className="act-b"
                    aria-label="Add another document"
                    data-tip="Add"
                    disabled={uploading}
                    onClick={onUploadClick}
                  >
                    <PlusCircleIcon />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="ccr-fg" style={{ marginBottom: 0 }}>
        <input
          id={`${idPrefix}-doc-reason-${doc.id}`}
          className={inpClass(Boolean(reasonDisplayError))}
          type="text"
          value={doc.skipReason}
          disabled={hasFile}
          placeholder={resolvedReasonPlaceholder}
          onChange={(e) => {
            const { value, limitExceeded } = boundedText(e.target.value, DOC_SKIP_REASON_MAX);
            onReasonChange(value);
            setLimitError(maxLengthLimitError(limitExceeded));
          }}
        />
        <FieldError message={reasonDisplayError} />
      </div>
    </div>
  );
}
